// Resmî hesabın "bildirimleri aç" kartı (9.26 · D-638).
//
// Batuhan: *"Bu mesajı daha kısa yapmalıyız ve öneri butonu olmalı ve
// bastığında direkt bildirim açılmalı."*
//
// İki şeyi birlikte yapmak zorunda, yoksa düğme yalan söyler:
//   1. Hesaptaki tercihleri açar — bildirimler varsayılan olarak kapalı (D-637).
//   2. Telefonun iznini ister — hiç sorulmamışsa. İzin verilmezse hesap açık,
//      telefon kapalı kalır ve yine hiçbir şey gelmez.
// İkincisi reddedilse bile birincisi geri alınmıyor: kişi izni sonra
// Ayarlar'dan verebilir, o zaman tercihleri zaten açık bulmalı.
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from './rn';
import CompactActionButton from './CompactActionButton';
import { api } from '../services/api';
import { getPushPermissionState, requestPushPermission } from '../notifications/pushLifecycle';
import {
  ENABLE_ALL_NOTIFICATIONS_PATCH,
  notificationsLookEnabled,
} from '../notifications/preferences';
import { useTranslation } from '../i18n/I18nContext';
import { useTheme } from '../theme/ThemeContext';
import { fontSize, radius, spacing, type ThemeColors } from '../theme';

type Props = {
  token: string;
  text: string;
  time: string;
};

type Durum = 'yukleniyor' | 'kapali' | 'aciliyor' | 'acik' | 'hata';

export default function NotificationOfferCard({ token, text, time }: Props) {
  const colors = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { t } = useTranslation();
  const [durum, setDurum] = useState<Durum>('yukleniyor');

  // Kart açılırken bugünkü hâli sor: zaten açmış birine "aç" demek yersiz.
  useEffect(() => {
    let etkin = true;
    api.getNotificationPreferences(token)
      .then((p) => { if (etkin) setDurum(notificationsLookEnabled(p) ? 'acik' : 'kapali'); })
      // Okunamazsa düğme yine gösterilsin: açmak zararsız ve tekrarlanabilir.
      .catch(() => { if (etkin) setDurum('kapali'); });
    return () => { etkin = false; };
  }, [token]);

  async function ac() {
    setDurum('aciliyor');
    try {
      await api.updateNotificationPreferences(token, ENABLE_ALL_NOTIFICATIONS_PATCH);
    } catch {
      setDurum('hata');
      return;
    }
    if (await getPushPermissionState() === 'undetermined') {
      await requestPushPermission(token).catch(() => undefined);
    }
    setDurum('acik');
  }

  return (
    <View style={styles.bubble} testID="notification-offer-card">
      <Text style={styles.text}>{text}</Text>
      {durum === 'acik' ? (
        <Text style={styles.done} testID="notification-offer-done">{t('message.notificationOfferEnabled')}</Text>
      ) : (
        <CompactActionButton
          label={t(durum === 'hata' ? 'common.retry' : 'message.notificationOfferEnable')}
          onPress={ac}
          variant="primary"
          loading={durum === 'aciliyor' || durum === 'yukleniyor'}
          disabled={durum === 'aciliyor' || durum === 'yukleniyor'}
          style={styles.button}
          testID="notification-offer-enable"
        />
      )}
      <Text style={styles.time}>{time}</Text>
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  bubble: {
    maxWidth: '80%',
    backgroundColor: colors.gray100,
    borderRadius: radius.card,
    borderBottomLeftRadius: 4,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  text: { fontSize: fontSize.md, color: colors.black, lineHeight: 21 },
  button: { alignSelf: 'stretch' },
  done: { fontSize: fontSize.sm, fontWeight: '600', color: colors.gray600 },
  time: { fontSize: 11, color: colors.gray400, alignSelf: 'flex-end' },
});
